/**
 * RequestAirSupport (#445 workstream 1, migration 0145).
 *
 * The operator-facing AIR-SUPPORT-REQUEST surface, mounted on the WILDFIRE incident work
 * surface as a SIBLING to LiveAircraftSection. In Ontario a waterbomber / air-support
 * aircraft is an MNRF/AFFES asset TASKED only by the MNRF fire centre (Ontario PERP §6.7.1);
 * this surface RECORDS a request on behalf of the requesting authority and routes it to the
 * fire centre (records-not-confers — the COP requests; it NEVER tasks the aircraft).
 *
 * BOTH the municipal and First-Nation paths request TO the fire centre. This mirrors the
 * PublicAlertRequestBlock (#497) shape — a SHORT request lifecycle (active → rescinded |
 * stood_down), NOT the drone accept/enroute/flown machine. The request states are ONLY
 * Requested / Routed to fire centre / Not yet routed — never a fabricated "dispatched / en
 * route / on station"; the ADS-B feed is NOT wired as fulfilment.
 *
 * FOREIGN-VALUE GUARD: the hazard/incident reference renders ONLY the COP's OWN doctrine-safe
 * identity — the linked incident's incident_number + the doctrine-safe HAZARD_LABEL map. A
 * hazard value NOT in the map falls back to the neutral 'Hazard' — NEVER the raw value.
 */
import { useCallback, useEffect, useState, type JSX } from 'react';
import {
  REQUEST_AIR_SUPPORT_LABEL,
  REQUEST_AIR_SUPPORT_FROM_FIRE_CENTRE_LABEL,
  RESCIND_AIR_SUPPORT_REQUEST_LABEL,
  STAND_DOWN_AIR_SUPPORT_REQUEST_LABEL,
  AIR_SUPPORT_REQUEST_HEADING,
  AIR_SUPPORT_RECORDS_NOT_CONFERS_NOTE,
  AIR_SUPPORT_AUTHORITY_KIND_LABELS,
  AIR_SUPPORT_REQUESTED_RESOURCES,
  AIR_SUPPORT_REQUESTED_RESOURCE_LABELS,
  AIR_SUPPORT_STATUS_LABELS,
  AIR_SUPPORT_ROUTED_STATE_PREFIX,
  AIR_SUPPORT_NOT_ROUTED_STATE_LABEL,
  type AirSupportAuthorityKind,
  type AirSupportRequestedResource,
  type AirSupportRequestRecord,
} from '@tsr/shared-types';
import {
  fetchAirSupportRequests,
  requestAirSupport,
  rescindAirSupportRequest,
  standDownAirSupportRequest,
} from './api';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';

const inputCls =
  'w-full rounded border border-slate-700 bg-slate-900/70 px-2 py-1 text-[12px] text-slate-100 focus:border-cyan-600 focus:outline-none';

/** Doctrine-safe hazard labels (FOREIGN-VALUE GUARD: an unknown value → 'Hazard', never raw). */
const HAZARD_LABEL: Record<string, string> = {
  wildfire: 'Wildfire',
  flood: 'Flood',
  ice_storm: 'Ice Storm',
  cap_warning: 'Public Alert',
  multi_hazard: 'Multi-Hazard',
  other: 'Incident',
};
function hazardLabel(hazardType: string | null | undefined): string {
  if (!hazardType) return '—';
  return HAZARD_LABEL[hazardType] ?? 'Hazard';
}

function whenLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function RequestAirSupport({
  incidentId,
  incidentNumber,
  hazardType,
  defaultAuthorityName,
  defaultAuthorityKind,
}: {
  /** The wildfire incident/source this request is a sibling to (matched on refresh). */
  incidentId?: string;
  /** The COP's OWN incident identifier (doctrine-safe) — the only incident reference rendered. */
  incidentNumber?: string | null;
  /** The COP's OWN hazard enum — rendered ONLY through HAZARD_LABEL (never raw). */
  hazardType?: string | null;
  /** Optional pre-seed for the requesting jurisdiction (operator can override). */
  defaultAuthorityName?: string | null;
  /** Optional pre-seed for the requesting-authority KIND (operator can still toggle). */
  defaultAuthorityKind?: AirSupportAuthorityKind;
}): JSX.Element {
  const [request, setRequest] = useState<AirSupportRequestRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [destinationName, setDestinationName] = useState<string | null>(null);

  // Request-form fields.
  const [authorityKind, setAuthorityKind] = useState<AirSupportAuthorityKind>(
    defaultAuthorityKind ?? 'municipal',
  );
  const [authorityName, setAuthorityName] = useState(defaultAuthorityName ?? '');
  const [requesterName, setRequesterName] = useState('');
  const [resource, setResource] = useState<AirSupportRequestedResource>('waterbomber');
  const [notes, setNotes] = useState('');

  const refresh = useCallback(() => {
    let alive = true;
    setLoading(true);
    void fetchAirSupportRequests()
      .then((all) => {
        if (!alive) return;
        setRequest(all.find((r) => (incidentId ? r.incident_id === incidentId : false)) ?? null);
      })
      .catch(() => {
        /* a read failure leaves the block in its empty state */
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [incidentId]);

  useEffect(() => refresh(), [refresh]);

  const doRequest = (): void => {
    if (busy || authorityName.trim().length === 0) return;
    setBusy(true);
    void requestAirSupport(
      {
        requesting_authority_kind: authorityKind,
        requesting_authority_name: authorityName.trim(),
        requester_name: requesterName.trim() || undefined,
        incident_id: incidentId,
        requested_resource: resource,
        hazard_type: hazardType ?? undefined,
        notes: notes.trim() || undefined,
      },
      getSessionId(),
    )
      .then((res) => {
        setDestinationName(res.destination_name);
        setRequest(res.request);
        setFormOpen(false);
        showToast(
          `Air support requested — ${res.request.request_number} · routed to the fire centre via ${res.delivery_channel}.`,
          { variant: 'success' },
        );
      })
      .catch((e) =>
        showToast((e as Error).message || 'Could not request air support.', { variant: 'error' }),
      )
      .finally(() => setBusy(false));
  };

  const doRescind = (): void => {
    if (busy || !request) return;
    setBusy(true);
    void rescindAirSupportRequest(request.id, getSessionId())
      .then((res) => {
        setDestinationName(res.destination_name);
        setRequest(res.request);
        showToast(`Request ${res.request.request_number} rescinded.`, { variant: 'success' });
      })
      .catch((e) => showToast((e as Error).message || 'Could not rescind.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const doStandDown = (): void => {
    if (busy || !request) return;
    setBusy(true);
    void standDownAirSupportRequest(request.id, getSessionId())
      .then((res) => {
        setDestinationName(res.destination_name);
        setRequest(res.request);
        showToast(`Request ${res.request.request_number} stood down.`, { variant: 'success' });
      })
      .catch((e) => showToast((e as Error).message || 'Could not stand down.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  return (
    <section
      data-testid="request-air-support-block"
      className="space-y-1.5 border-t border-slate-800 pt-3"
    >
      <h3 className="text-xs uppercase tracking-wider text-slate-400">
        {AIR_SUPPORT_REQUEST_HEADING}
      </h3>
      {/* records-not-confers footer — the seat REQUESTS; only MNRF/AFFES tasks the aircraft. */}
      <p className="text-[10px] text-slate-500" data-testid="air-support-records-not-confers">
        {AIR_SUPPORT_RECORDS_NOT_CONFERS_NOTE}
      </p>

      {loading ? (
        <p className="text-[11px] text-slate-500">Loading…</p>
      ) : !request ? (
        /* ── No request yet — the Request affordance (fire centre bound into the label) ── */
        !formOpen ? (
          <button
            type="button"
            data-testid="request-air-support-open"
            onClick={() => setFormOpen(true)}
            className="w-full rounded border border-cyan-500/60 py-1.5 text-[11px] text-cyan-200 hover:bg-cyan-600/20"
          >
            {REQUEST_AIR_SUPPORT_FROM_FIRE_CENTRE_LABEL}
          </button>
        ) : (
          <div className="space-y-1.5 rounded border border-slate-700 bg-slate-900/50 p-2">
            {/* Requested resource — waterbomber / helicopter / recon (the COP's OWN vocab). */}
            <label className="block text-[10px] text-slate-400">
              Requested resource
              <select
                data-testid="air-support-resource"
                value={resource}
                onChange={(e) => setResource(e.target.value as AirSupportRequestedResource)}
                className={inputCls}
              >
                {AIR_SUPPORT_REQUESTED_RESOURCES.map((r) => (
                  <option key={r} value={r}>
                    {AIR_SUPPORT_REQUESTED_RESOURCE_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
            {/* Authority-kind toggle — municipal vs First Nation (both request TO the fire centre). */}
            <div className="flex gap-1" role="group" aria-label="Requesting authority kind">
              {(['municipal', 'first_nation'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`air-support-authority-kind-${k}`}
                  onClick={() => setAuthorityKind(k)}
                  className={`flex-1 rounded border py-1 text-[11px] ${
                    authorityKind === k
                      ? 'border-cyan-500 bg-cyan-600/25 text-cyan-100'
                      : 'border-slate-700 text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  {AIR_SUPPORT_AUTHORITY_KIND_LABELS[k]}
                </button>
              ))}
            </div>
            <label className="block text-[10px] text-slate-400">
              {authorityKind === 'first_nation' ? 'Requesting First Nation' : 'Requesting municipality'}
              <input
                data-testid="air-support-authority-name"
                value={authorityName}
                onChange={(e) => setAuthorityName(e.target.value)}
                placeholder={
                  authorityKind === 'first_nation'
                    ? 'e.g. Eabametoong First Nation'
                    : 'e.g. City of Kenora'
                }
                className={inputCls}
              />
            </label>
            <label className="block text-[10px] text-slate-400">
              Requested by (optional)
              <input
                data-testid="air-support-requester-name"
                value={requesterName}
                onChange={(e) => setRequesterName(e.target.value)}
                placeholder="name / title (optional)"
                className={inputCls}
              />
            </label>
            <label className="block text-[10px] text-slate-400">
              Notes (optional)
              <textarea
                data-testid="air-support-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className={inputCls}
              />
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="request-air-support-submit"
                onClick={doRequest}
                disabled={busy || authorityName.trim().length === 0}
                className="flex-1 rounded bg-cyan-600 py-1.5 text-[11px] font-medium text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? 'Requesting…' : REQUEST_AIR_SUPPORT_LABEL}
              </button>
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="rounded border border-slate-700 px-3 py-1.5 text-[11px] text-slate-400 hover:bg-slate-800"
              >
                Cancel
              </button>
            </div>
          </div>
        )
      ) : (
        /* ── A request exists — record + honest state + actions ─────────────── */
        <div className="space-y-1.5" data-testid="air-support-request-detail">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[12px] text-slate-200">{request.request_number}</span>
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                request.status === 'active'
                  ? 'bg-cyan-600/25 text-cyan-200'
                  : 'bg-slate-700/50 text-slate-400'
              }`}
              data-testid="air-support-status"
            >
              {AIR_SUPPORT_STATUS_LABELS[request.status]}
            </span>
          </div>
          <dl className="grid grid-cols-[7rem_1fr] gap-y-0.5 text-[11px]">
            <dt className="text-slate-400">Resource</dt>
            <dd className="text-slate-200" data-testid="air-support-resource-value">
              {AIR_SUPPORT_REQUESTED_RESOURCE_LABELS[request.requested_resource]}
            </dd>
            <dt className="text-slate-400">Authority</dt>
            <dd className="text-slate-200">
              {request.requesting_authority_name}
              {' · '}
              {AIR_SUPPORT_AUTHORITY_KIND_LABELS[request.requesting_authority_kind]}
            </dd>
            {request.requester_name && (
              <>
                <dt className="text-slate-400">Requested by</dt>
                <dd className="text-slate-200">{request.requester_name}</dd>
              </>
            )}
            {/* FOREIGN-VALUE GUARD: incident + hazard render ONLY the COP's own identity. */}
            <dt className="text-slate-400">Hazard</dt>
            <dd className="text-slate-300" data-testid="air-support-hazard">
              {hazardLabel(request.hazard_type)}
            </dd>
            <dt className="text-slate-400">Incident</dt>
            <dd className="text-slate-300" data-testid="air-support-incident">
              {incidentNumber ?? '—'}
            </dd>
            <dt className="text-slate-400">Requested</dt>
            <dd className="text-slate-300">{whenLabel(request.requested_at)}</dd>
            {request.status === 'rescinded' && (
              <>
                <dt className="text-slate-400">Rescinded</dt>
                <dd className="text-slate-300">{whenLabel(request.rescinded_at)}</dd>
              </>
            )}
            {request.status === 'stood_down' && (
              <>
                <dt className="text-slate-400">Stood down</dt>
                <dd className="text-slate-300">{whenLabel(request.stood_down_at)}</dd>
              </>
            )}
            {/* HONEST STATE — Routed to fire centre {ts} | Not yet routed. NEVER "dispatched". */}
            <dt className="text-slate-400">Fire centre</dt>
            <dd className="text-slate-300" data-testid="air-support-route-state">
              {request.fire_centre_notified_at
                ? `${AIR_SUPPORT_ROUTED_STATE_PREFIX} ${whenLabel(request.fire_centre_notified_at)}`
                : AIR_SUPPORT_NOT_ROUTED_STATE_LABEL}
              {destinationName ? ` · ${destinationName}` : ''}
            </dd>
          </dl>

          {request.status === 'active' && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="rescind-air-support-request"
                onClick={doRescind}
                disabled={busy}
                className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800 disabled:opacity-50"
              >
                {RESCIND_AIR_SUPPORT_REQUEST_LABEL}
              </button>
              <button
                type="button"
                data-testid="stand-down-air-support-request"
                onClick={doStandDown}
                disabled={busy}
                className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800 disabled:opacity-50"
              >
                {STAND_DOWN_AIR_SUPPORT_REQUEST_LABEL}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
